import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical, ArrowsOutCardinal } from "@phosphor-icons/react";

/**
 * SortableWidget — wraps a dashboard widget so it can be dragged to reorder
 * when `editMode` is on. Uses @dnd-kit for both mouse and touch support.
 *
 * Edit-mode UX (revamped):
 *   - Container gets a dashed primary ring so the boundary is unmistakable
 *   - Big grab handle in the top-left corner with the widget label — pill-
 *     shaped, animated shadow, clear "Drag" affordance
 *   - Corner "move" glyph in every corner reinforces that the whole card
 *     can be manipulated
 *   - Slight scale-down on drag + higher z-index so overlaps look natural
 */
export default function SortableWidget({ id, editMode, label, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !editMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : "auto",
    position: "relative",
    scale: isDragging ? "0.98" : "1",
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`widget-${id}`}>
      {editMode && (
        <>
          {/* Prominent drag handle — sits ABOVE the widget so it never
              overlaps card content. Distinct pill with icon + label, shadow
              intensifies on grab. */}
          <div
            className="absolute -top-4 left-4 z-20 flex items-center gap-1.5 bg-primary text-primary-foreground text-[11px] uppercase tracking-wider font-bold pl-2 pr-3 py-1.5 rounded-full shadow-lg hover:shadow-xl cursor-grab active:cursor-grabbing active:scale-95 touch-none select-none transition-all"
            {...attributes}
            {...listeners}
            data-testid={`drag-handle-${id}`}
            aria-label={`Drag to reorder ${label}`}
          >
            <DotsSixVertical size={14} weight="bold" />
            <span>Drag · {label}</span>
          </div>

          {/* Corner glyph — visual reinforcement that the whole card can
              be moved. Non-interactive; the handle above owns the drag. */}
          <ArrowsOutCardinal
            size={18}
            weight="bold"
            className="absolute -top-2 -right-2 z-10 text-primary bg-white rounded-full p-0.5 shadow ring-2 ring-primary/40 pointer-events-none"
          />
        </>
      )}
      <div className={editMode ? "ring-2 ring-primary/60 ring-offset-2 ring-offset-background rounded-xl border-2 border-dashed border-primary/40" : ""}>
        {children}
      </div>
    </div>
  );
}
